import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_LABELS: Record<string, string> = {
  RASCUNHO: 'Rascunho', ENVIADO: 'Enviado', APROVADO: 'Aprovado',
  REJEITADO: 'Rejeitado', CANCELADO: 'Cancelado',
};

export default function OrcamentosPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');

  const { data: orcamentos = [], isLoading } = useQuery({
    queryKey: ['orcamentos', statusFilter],
    queryFn: () => api.get('/orcamentos', { params: { status: statusFilter || undefined } }).then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/orcamentos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamentos'] }),
  });

  function handleDelete(e: React.MouseEvent, o: any) {
    e.stopPropagation();
    if (window.confirm(`Excluir o orçamento ${o.numero}? Essa ação não pode ser desfeita.`)) {
      deleteMutation.mutate(o.id);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Orçamentos</h1>
        <button onClick={() => navigate('/orcamentos/novo')} className="btn-primary flex items-center gap-1">
          <span>+</span> Novo
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2">
        {['', 'RASCUNHO', 'ENVIADO', 'APROVADO', 'REJEITADO'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === s ? 'bg-primary-600 text-white' : 'bg-white border border-gray-300 text-gray-600'
            }`}
          >
            {s === '' ? 'Todos' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : orcamentos.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-2">📋</div>
          <p>Nenhum orçamento encontrado</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orcamentos.map((o: any) => (
            <div
              key={o.id}
              onClick={() => navigate(`/orcamentos/${o.id}`)}
              className="card cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-sm text-primary-700">{o.numero}</div>
                  <div className="font-medium mt-0.5">{o.cliente?.nome}</div>
                  <div className="text-sm text-gray-500">{o.cliente?.telefone}</div>
                </div>
                <div className="text-right">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium badge-${o.status.toLowerCase()}`}>
                    {STATUS_LABELS[o.status]}
                  </span>
                  {o.totalLiquido > 0 && (
                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      R$ {o.totalLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span>{o._count?.itens || 0} itens</span>
                  <span>{o.responsavel?.nome}</span>
                  <span>{new Date(o.criadoEm).toLocaleDateString('pt-BR')}</span>
                </div>
                <button
                  onClick={e => handleDelete(e, o)}
                  disabled={deleteMutation.isPending}
                  className="text-red-500 text-sm"
                  title="Excluir orçamento"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
